import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../components/ui/ModalProvider";
import UserStatusBadge from "../../components/users/UserStatusBadge";
import userService from "../../services/userService";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import {
  ArrowLeft, Globe, Building2, MapPin, Link2, Calendar,
  Clock, FileText, ChevronDown, ChevronUp, Activity,
  Cpu, AlertTriangle, CheckCircle, XCircle, Layers,
} from "lucide-react";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

// ── Collapsible list ────────────────────────────────────────────────────────
function CollapsibleItems({ items = [], maxShown = 3, renderItem, emptyText = "None" }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, maxShown);
  const extra = items.length - maxShown;
  if (items.length === 0)
    return <p className="text-sm italic text-slate-400">{emptyText}</p>;
  return (
    <div>
      <div className="space-y-2">{shown.map(renderItem)}</div>
      {items.length > maxShown && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Show less" : `Show ${extra} more`}
        </button>
      )}
    </div>
  );
}

// ── Section card wrapper ────────────────────────────────────────────────────
function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-slate-400" />}
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── Stat card ───────────────────────────────────────────────────────────────
function Stat({ label, value, bg, color }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
      <p className="text-2xl font-bold text-slate-900">{value ?? 0}</p>
      <div className="mt-1 flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

// ── Status badge for articles/pins ─────────────────────────────────────────
const STATUS_COLORS = {
  published: "bg-green-100 text-green-700",
  completed: "bg-blue-100 text-blue-700",
  error: "bg-red-100 text-red-700",
  failed: "bg-red-100 text-red-700",
  queued: "bg-yellow-100 text-yellow-700",
  generating: "bg-purple-100 text-purple-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  ready: "bg-slate-100 text-slate-600",
};
function StatusPill({ status }) {
  const cls = STATUS_COLORS[status?.toLowerCase()] || "bg-slate-100 text-slate-500";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status || "unknown"}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm, alert } = useModal();
  const { user, isManager, loading: authLoading, profileLoading } = useAuth();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (!authLoading && !profileLoading && !isManager) {
      navigate("/automation-control-panel");
    }
  }, [authLoading, profileLoading, isManager, navigate]);

  useEffect(() => {
    if (!id) return;
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await userService.fetchUserDetails(id);
    if (error) setError("Unable to load user details.");
    else setDetails(data);
    setLoading(false);
  };

  const handleRoleChange = async (nextRole) => {
    if (!details?.profile || !user?.id || details.profile.role === nextRole) return;
    const confirmed = await confirm(
      `Change ${details.profile.email}'s role to "${nextRole}"?`,
      { confirmText: "Update role" }
    );
    if (!confirmed) return;
    setProcessing({ action: "role" });
    const { error } = await userService.updateUserRole(details.profile.id, nextRole, user.id);
    setProcessing(null);
    if (error) { alert("Failed to update role."); return; }
    fetchDetails();
  };

  const handleStatusToggle = async () => {
    if (!details?.profile || !user?.id) return;
    const nextStatus = !details.profile.is_active;
    const title = nextStatus ? "Enable account" : "Disable account";
    const confirmed = await confirm(
      `${title} for ${details.profile.email}? Automation access will be affected.`,
      { confirmText: title }
    );
    if (!confirmed) return;
    setProcessing({ action: "status" });
    const { error } = await userService.updateUserStatus(details.profile.id, nextStatus, user.id);
    setProcessing(null);
    if (error) { alert("Failed to update status."); return; }
    fetchDetails();
  };

  const automationRuns = useMemo(() => details?.automationRuns || [], [details]);

  // ── Loading / auth guard ──────────────────────────────────────────────────
  if (loading || authLoading || profileLoading || !isManager) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!details?.profile) {
    return (
      <div className="p-8 text-sm text-slate-500">{error || "User not found."}</div>
    );
  }

  const {
    profile,
    sites = [],
    automationSummary = {},
    pinterestAccounts = [],
    facebookPages = [],
    recentArticles = [],
    recentPins = [],
    recentErrors = [],
    recentActivity = [],
    auditLogs = [],
  } = details;

  const articles = automationSummary?.articles || {};
  const pins = automationSummary?.pins || {};
  const isProcessingRole = processing?.action === "role";
  const isProcessingStatus = processing?.action === "status";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="main-content">
          <div className="mx-auto max-w-6xl space-y-6 px-2 py-6">

            {/* ── Header ── */}
            <div>
              <button
                onClick={() => navigate("/admin/users")}
                className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All Users
              </button>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">
                  {profile.full_name || profile.email}
                </h1>
                <UserStatusBadge isActive={profile.is_active} />
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {profile.email}
                {profile.role && (
                  <span className="ml-3 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 capitalize">
                    {profile.role}
                  </span>
                )}
                <span className="ml-3">Joined {fmtDate(profile.created_at)}</span>
              </p>
            </div>

            {/* ── Stat cards ── */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
              <Stat label="Articles Generated" value={articles.total} color="#2563EB" />
              <Stat label="Articles Published" value={articles.published} color="#16A34A" />
              <Stat label="Pins Generated" value={pins.total} color="#9333EA" />
              <Stat label="Pins Published" value={pins.published} color="#EA580C" />
              <Stat label="WP Sites" value={sites.length} color="#1D4ED8" />
              <Stat label="Pinterest Accs" value={pinterestAccounts.length} color="#E60023" />
              <Stat label="Facebook Pages" value={facebookPages.length} color="#1877F2" />
              <Stat label="Failed Jobs" value={articles.error} color="#DC2626" />
            </div>

            {/* ── Main grid ── */}
            <div className="grid gap-6 lg:grid-cols-3">

              {/* Left column */}
              <div className="space-y-6">

                {/* Profile info */}
                <Card title="Profile" icon={Activity}>
                  <div className="space-y-3 text-sm">
                    {profile.bio && (
                      <p className="text-slate-600 italic">"{profile.bio}"</p>
                    )}
                    {[
                      profile.company && { icon: Building2, label: profile.company },
                      profile.location && { icon: MapPin, label: profile.location },
                      profile.website && { icon: Globe, label: profile.website, href: profile.website },
                    ].filter(Boolean).map(({ icon: Icon, label, href }, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-500">
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {href
                          ? <a href={href} target="_blank" rel="noreferrer" className="truncate text-blue-600 hover:underline">{label}</a>
                          : <span className="truncate">{label}</span>
                        }
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-slate-500">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      <span>Created {fmt(profile.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      <span>Updated {fmt(profile.updated_at)}</span>
                    </div>
                    {recentActivity[0] && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Activity className="h-3.5 w-3.5 shrink-0" />
                        <span>Last seen {fmt(recentActivity[0].lastSeen)}</span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Actions */}
                <Card title="Actions" icon={Cpu}>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Role
                      </label>
                      <select
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={profile.role || "user"}
                        disabled={isProcessingRole}
                        onChange={(e) => handleRoleChange(e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <button
                      disabled={isProcessingStatus}
                      onClick={handleStatusToggle}
                      className={`w-full rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
                        profile.is_active
                          ? "bg-red-50 text-red-700 hover:bg-red-100"
                          : "bg-green-50 text-green-700 hover:bg-green-100"
                      }`}
                    >
                      {isProcessingStatus
                        ? "Updating…"
                        : profile.is_active
                        ? "Disable Account"
                        : "Enable Account"}
                    </button>
                  </div>
                </Card>

              </div>

              {/* Right column (2/3) */}
              <div className="space-y-6 lg:col-span-2">

                {/* Integrations */}
                <Card title="Connected Integrations" icon={Link2}>
                  <div className="grid gap-6 sm:grid-cols-3">
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        WordPress Sites
                      </p>
                      <CollapsibleItems
                        items={sites}
                        maxShown={3}
                        emptyText="No sites connected"
                        renderItem={(site) => (
                          <div key={site.id} className="rounded-lg border border-slate-100 p-2.5">
                            <a
                              href={site.site_url}
                              target="_blank"
                              rel="noreferrer"
                              className="block truncate text-sm font-medium text-slate-800 hover:text-blue-600"
                            >
                              {site.site_name}
                            </a>
                            <p className="mt-0.5 text-xs text-slate-400 capitalize">
                              {site.status || "unknown"}
                              {site.last_sync && <> · Synced {fmtDate(site.last_sync)}</>}
                            </p>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Pinterest Accounts
                      </p>
                      <CollapsibleItems
                        items={pinterestAccounts}
                        maxShown={3}
                        emptyText="No accounts connected"
                        renderItem={(acc) => (
                          <div key={acc.id} className="rounded-lg border border-slate-100 p-2.5">
                            <p className="text-sm font-medium text-slate-800">@{acc.username}</p>
                            <p className="mt-0.5 text-xs text-slate-400">Connected {fmtDate(acc.created_at)}</p>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Facebook Pages
                      </p>
                      <CollapsibleItems
                        items={facebookPages}
                        maxShown={3}
                        emptyText="No pages selected"
                        renderItem={(page) => (
                          <div key={page.id} className="rounded-lg border border-slate-100 p-2.5">
                            <p className="text-sm font-medium text-slate-800">{page.page_name}</p>
                            <p className="mt-0.5 text-xs text-slate-400">Added {fmtDate(page.created_at)}</p>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </Card>

                {/* Recent Activity */}
                <Card title="Recent Sessions" icon={Clock}>
                  <CollapsibleItems
                    items={recentActivity}
                    maxShown={3}
                    emptyText="No sessions recorded"
                    renderItem={(s) => (
                      <div key={s.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 p-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs text-slate-400">{s.userAgent || "Unknown device"}</p>
                          <p className="mt-0.5 text-xs text-slate-400">IP: {s.ip || "—"}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xs font-medium text-slate-700">Last seen</p>
                          <p className="text-xs text-slate-400">{fmt(s.lastSeen)}</p>
                        </div>
                      </div>
                    )}
                  />
                </Card>

              </div>
            </div>

            {/* ── Articles + Pins ── */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Card title="Recent Articles" icon={FileText}
                action={<span className="text-xs text-slate-400">{recentArticles.length} shown</span>}>
                <CollapsibleItems
                  items={recentArticles}
                  maxShown={5}
                  emptyText="No articles yet"
                  renderItem={(a) => (
                    <div key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {a.title || a.focus_keyword || "Untitled"}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">{fmtDate(a.created_at)}</p>
                        {a.error_message && (
                          <p className="mt-1 text-xs text-red-600 line-clamp-1">{a.error_message}</p>
                        )}
                      </div>
                      <StatusPill status={a.status} />
                    </div>
                  )}
                />
              </Card>

              <Card title="Recent Pins" icon={Layers}
                action={<span className="text-xs text-slate-400">{recentPins.length} shown</span>}>
                <CollapsibleItems
                  items={recentPins}
                  maxShown={5}
                  emptyText="No pins yet"
                  renderItem={(p) => (
                    <div key={p.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {p.pin_title || "Untitled pin"}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">{fmtDate(p.created_at)}</p>
                      </div>
                      <StatusPill status={p.status} />
                    </div>
                  )}
                />
              </Card>
            </div>

            {/* ── Automation Runs ── */}
            <Card title="Automation Runs" icon={Cpu}
              action={
                automationSummary?.lastAutomationRun && (
                  <span className="text-xs text-slate-400">
                    Last run {fmt(automationSummary.lastAutomationRun)}
                  </span>
                )
              }>
              <CollapsibleItems
                items={automationRuns}
                maxShown={5}
                emptyText="No automation runs yet"
                renderItem={(run) => (
                  <div key={run.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                        {run.type}
                      </span>
                      <p className="truncate text-sm text-slate-700">{run.label}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <StatusPill status={run.status} />
                      <span className="text-xs text-slate-400">{fmt(run.timestamp)}</span>
                    </div>
                  </div>
                )}
              />
            </Card>

            {/* ── Errors + Audit ── */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Card title="Recent Errors" icon={AlertTriangle}>
                <CollapsibleItems
                  items={recentErrors}
                  maxShown={3}
                  emptyText="No recent errors"
                  renderItem={(e, i) => (
                    <div key={`${e.id}-${i}`} className="rounded-lg border border-red-100 bg-red-50 p-3">
                      <p className="text-sm font-medium text-slate-800">
                        {e.focus_keyword || e.title || "Article"}
                      </p>
                      <p className="mt-1 text-xs text-red-700 line-clamp-2">
                        {e.error_message || "Error recorded"}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">{fmt(e.updated_at || e.created_at)}</p>
                    </div>
                  )}
                />
              </Card>

              {auditLogs.length > 0 && (
                <Card title="Audit Log" icon={CheckCircle}>
                  <CollapsibleItems
                    items={auditLogs}
                    maxShown={3}
                    emptyText="No audit entries"
                    renderItem={(log) => (
                      <div key={log.id} className="rounded-lg border border-slate-100 p-3">
                        <p className="text-sm font-medium text-slate-800">{log.action}</p>
                        <p className="mt-0.5 text-xs text-slate-400">{fmt(log.created_at)}</p>
                      </div>
                    )}
                  />
                </Card>
              )}
            </div>

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
